<div class="container px-4 py-8 mx-auto">
  <!-- Header section - hidden when addEmployee=true -->
  <div
    *ngIf="!addEmployee"
    class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8"
  >
    <div>
      <h1 class="text-2xl md:text-3xl font-bold text-gray-900">{{ 'DASHBOARD.MY_COMPANIES.TITLE' | translate }}</h1>
      <p class="text-gray-600 mt-1">
        {{ 'DASHBOARD.MY_COMPANIES.DESCRIPTION' | translate }}
      </p>
    </div>
    <button
      (click)="addNewCompany()"
      class="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2.5 rounded-lg text-white font-medium transition-all shadow-sm hover:shadow-md"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="opacity-90"
      >
        <path d="M12 5v14m-7-7h14" />
      </svg>
      {{ 'DASHBOARD.MY_COMPANIES.ADD_COMPANY' | translate }}
    </button>
  </div>

  <!-- Loading state -->
  <div *ngIf="isLoading" class="flex justify-center py-12">
    <app-loader></app-loader>
  </div>

  <!-- Error state -->
  <div *ngIf="!isLoading && error" class="text-center py-12">
    <p class="text-red-600 font-medium">{{ error }}</p>
  </div>

  <!-- Empty state -->
  <div *ngIf="!isLoading && !error && companies.length === 0" class="text-center py-12">
    <p class="text-gray-600">
      {{'DASHBOARD.MY_COMPANIES.NO_COMPANIES_TITLE' | translate }}
    </p>
  </div>

  <!-- Companies grid -->
  <div
    *ngIf="!isLoading && !error && companies.length > 0"
    class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
  >
    <!-- Company Card -->
    <div
      *ngFor="let company of companies"
      class="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-all duration-300 text-center"
      [class.cursor-pointer]="addEmployee"
      (click)="addEmployee ? viewCompanyDetails(company.id) : null"
    >
      <!-- Company Name -->
      <div class="flex flex-col items-center mb-4">
        <div class="flex items-center space-x-2 mb-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-teal-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 10h16M4 14h16M4 18h16"
            />
          </svg>
          <h3 class="text-lg font-semibold text-gray-900 truncate">
            {{ company.name }}
          </h3>
        </div>
        <p class="text-gray-500 text-sm truncate">
          {{ company.companyNameArabic }}
        </p>
      </div>

      <!-- Company Details -->
      <div class="space-y-2 text-left">
        <!-- CR Number -->
        <div class="flex justify-between text-sm">
          <span class="text-gray-500">{{ 'DASHBOARD.MY_COMPANIES.CR_NUMBER' | translate }}</span>
          <span class="font-medium text-gray-900">
            {{ company.crNumber || ('DASHBOARD.MY_COMPANIES.NOT_PROVIDED' | translate) }}
          </span>
        </div>

        <!-- Status -->
        <div class="flex justify-between text-sm items-center">
          <span class="text-gray-500">{{ 'DASHBOARD.MY_COMPANIES.STATUS' | translate }}</span>
          <span
            *ngIf="company.status?.toLowerCase() === 'success'; else notVerified"
            class="px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-md border border-green-200"
          >
            {{ 'DASHBOARD.MY_COMPANIES.VERIFIED' | translate }}
          </span>
          <ng-template #notVerified>
            <span
              class="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-md border border-gray-200"
            >
              {{ 'DASHBOARD.MY_COMPANIES.NOT_VERIFIED' | translate }}
            </span>
          </ng-template>
        </div>

        <!-- Employees -->
        <div class="flex justify-between text-sm items-center">
          <span class="text-gray-500">{{ 'DASHBOARD.MY_COMPANIES.EMPLOYEES' | translate }}</span>
          <div class="flex items-center gap-1 font-medium text-gray-900">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {{ company.numberOfEmployees || 'N/A' }}
          </div>
        </div>

        <!-- Entity 700 Number -->
        <div class="flex justify-between text-sm" *ngIf="company.entity700Number">
          <span class="text-gray-500">Entity 700</span>
          <span class="font-medium text-gray-900">
            {{ company.entity700Number }}
          </span>
        </div>
      </div>

      <!-- Action Button -->
      <div class="mt-5">
        <button
          *ngIf="!addEmployee"
          (click)="viewCompanyDetails(company.id)"
          class="w-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 font-medium rounded-lg py-2 transition-all"
        >
          {{ 'DASHBOARD.MY_COMPANIES.VIEW_DETAILS' | translate }}
        </button>

        <button
          *ngIf="addEmployee"
          (click)="viewCompanyDetails(company.id)"
          class="w-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 font-medium rounded-lg py-2 transition-all flex items-center justify-center gap-2"
        >
          <svg
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          {{ 'DASHBOARD.MY_COMPANIES.SELECT' | translate }}
        </button>
      </div>
    </div>
  </div>
</div>